// index.js - Enhanced with global error handling and notification system
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './index.css';

// Components
import Login from './components/Login';
import Register from './components/Register';
import VerifyEmail from './components/VerifyEmail';
import InvitationManager from './components/admin/InvitationManager';
import TestPage from './components/TestPage';
import Workflow from './components/Workflow';
import Agent from './components/Agent';
import HomePage from './home_page';
import ChoicePage from './components/ChoicePage';
import ProtectedRoute from './components/ProtectedRoute';

// Global error handling and notifications
import ErrorBoundary from './components/ErrorBoundary';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import { setGlobalNotificationContext } from './utils/alertReplacer';

import reportWebVitals from './reportWebVitals';
import { ReactFlowProvider } from 'reactflow';
import { I18nProvider } from './i18n';

// Component to initialize global notification context
const NotificationContextSetter = () => {
  const notificationContext = useNotification();
  
  React.useEffect(() => {
    // Set global notification context for alertReplacer
    setGlobalNotificationContext(notificationContext);
  }, [notificationContext]);
  
  return null;
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
<React.StrictMode>
  {/* Wrap entire app in error boundary to catch any unhandled errors */}
  <ErrorBoundary
    onError={(error, errorInfo) => {
      // Optional: Send error to logging service in production
      if (process.env.NODE_ENV === 'production') {
        // Example: Send to error tracking service
        // errorTrackingService.captureException(error, errorInfo);
        console.error('Global error boundary caught:', error);
      }
    }}
  >
    {/* Notification provider for toast notifications */}
    <NotificationProvider>
      <NotificationContextSetter />
      <I18nProvider defaultLang={(localStorage.getItem('i18n_lang') || 'en')}>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/admin/invitations" element={<InvitationManager />} />
          <Route
            path="/choice"
            element={
              <ProtectedRoute>
                <ChoicePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/workflow"
            element={
              <ProtectedRoute>
                <ReactFlowProvider>
                  <Workflow />
                </ReactFlowProvider>
              </ProtectedRoute>
            }
          />
          {/* M19.5 Agent tab: Django->harness auth bridge (INV-M19.5-AGENT-TAB-SENDER-001) */}
          <Route
            path="/agent"
            element={
              <ProtectedRoute>
                <Agent />
              </ProtectedRoute>
            }
          />
          {/* Deprecated simplified workflow route retained for backward-compat; redirect to /workflow */}
          <Route path="/simplified-workflow" element={<Navigate to="/workflow" replace />} />
          <Route path="/" element={<HomePage />} />
          <Route
            path="/test"
            element={
                <TestPage />
            }
          />
        </Routes>
      </Router>
      </I18nProvider>
    </NotificationProvider>
  </ErrorBoundary>
</React.StrictMode>
);

reportWebVitals();
